@file:OptIn(ExperimentalSerializationApi::class)

package playlist

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import kotlinx.serialization.protobuf.ProtoNumber
import org.ssoft.common.tools.MAX
import org.ssoft.common.tools.MIN
import playlist.ImplPlaylist.ImplSettingsCompany

interface ImplPlaylist<C : ImplPlaylist.ImplCampaign<V>, V : ImplPlaylist.ImplVisual, S : ImplSettingsCompany> {
    var deviceId: Long
    var settingsCompany: S
    var campaigns: MutableList<C>

    interface ImplCampaign<V : ImplVisual> {
        var campaignId: Long
        var campaignGroupId: Long
        var companyId: Long
        var isCommercial: Boolean
        var isNonCommercial: Boolean
        var nameCampaign: String
        var itemDuration: Long
        var itemCount: Int
        var position: Int
        var volume: Int
        var start: LocalDate
        var end: LocalDate
        var excludedDoY: List<Int>
        var apps: List<AppConfig>
        var visualRotaryMode: Int

        /**
         * @see api.AppBehavior
         */
        var appBehavior: Int
        var offsets: List<Long>


    }

    interface ImplVisual {
        var resourceId: Long
        var campaignResId: Long
        var campaignVisualId: Long
        var duration: Long
        var type: Int
        var width: Int
        var height: Int
        var isLand: Boolean
        var conditions: Conditions

        @Deprecated("use from conditions", replaceWith = ReplaceWith("conditions.days"))
        var days: Int

        @Deprecated("use from conditions", replaceWith = ReplaceWith("conditions.time.start"))
        var timeStart: LocalTime

        @Deprecated("use from conditions", replaceWith = ReplaceWith("conditions.time.end"))
        var timeEnd: LocalTime
    }

    @Serializable
    data class Conditions(
        /** @see api.DayOfWeek */
        @ProtoNumber(1)
        var days: Int,
        @ProtoNumber(2)
        var time: Time,
        @ProtoNumber(3)
        var weather: Weather = Weather()
    ) {
        @Serializable
        data class Time(
            @ProtoNumber(1)
            var start: LocalTime,
            @ProtoNumber(2)
            var end: LocalTime
        )

        @Serializable
        data class Weather(
            @ProtoNumber(1)
            var temperature: WeatherRange = WeatherRange(false, -100, 100),
            @ProtoNumber(2)
            var wind: WeatherRange = WeatherRange(false, 0, 50),
            @ProtoNumber(3)
            var humidity: WeatherRange = WeatherRange(false, 0, 100),
            @ProtoNumber(4)
            var pictogram: WeatherPictograms = WeatherPictograms(false)
        )

        @Serializable
        data class WeatherRange(
            @ProtoNumber(1)
            var enable: Boolean,
            @ProtoNumber(2)
            var min: Int,
            @ProtoNumber(3)
            var max: Int
        )

        @Serializable
        data class WeatherPictograms(
            @ProtoNumber(1)
            var enable: Boolean,
            @ProtoNumber(2)
            var id: MutableList<Long> = mutableListOf(),
            @ProtoNumber(3)
            var name: List<String> = listOf()
        )
    }

    interface ImplSettingsCompany {
        var campaignType: Int
        var entityCountDefault: Int
        var entityDurationDefault: Long
        var defaultVisual: Long
    }

    @Serializable
    data class AppConfig(
        var appId: Long = -1L,
        var name: String = "",
        var logo: Long = -1,
        var color: CodeColor = CodeColor(),
        var codeLand: CodeConfig = CodeConfig(
            35, 10, 89, 80
        ),
        var codePort: CodeConfig = CodeConfig(
            35, 10, 80, 89
        ),
        var conditions: Conditions = Conditions(
            days = -1,
            time = Conditions.Time(
                start = LocalTime.MIN,
                end = LocalTime.MAX
            )
        )
    )

    @Serializable
    data class CodeColor(
        var fg: Long = 0xFF_00_00_00,
        var bg: Long = 0xFF_FF_FF_FF,
    )

    @Serializable
    data class CodeConfig(
        var sizePct: Int = 35,
        var offsetPct: Int = 10,
        var xPct: Int = 50,
        var yPct: Int = 50,
    )
}
